// @flow

import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { Expand } from '../svg';
import ListItem from '../list/ListItem';
import { breakpoint } from '../../utils/responsive';

type Props = {
  id: string,
  children: React.ChildrenArray<React.Element<typeof ListItem>>,
  selectedIndex?: number,
  onSelect?: (newIndex: number) => void,
};

const transitionSpeed = '200ms';

const ListContainer = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ListItemContainer = styled.li`
  display: block;
  width: 100%;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    background-color: currentColor;
    bottom: -1px;
    height: 1px;
    z-index: 10;
    opacity: ${(props) => (props.selected ? 0.5 : 0.1)};
    transition: opacity ${transitionSpeed} ease-in-out;
  }

  &:hover {
    background-color: ${(props) =>
      props.selected
        ? ''
        : props.theme && props.theme.accordionList
        ? props.theme.accordionList.hoverColor
        : 'currentColor'};
    color: ${(props) =>
      props.selected
        ? ''
        : props.theme
        ? props.theme.actionColor
        : 'currentColor'};
  }
`;

const ListItemTitle = styled.div`
  display: table;
  width: 100%;
  padding: 1rem 0;
  position: relative;
  color: currentColor;
  cursor: pointer;
  ${breakpoint('medium')`padding: 1rem;`};
`;

const ListItemTitleText = styled.span`
  font-weight: ${(props) => (props.selected ? '600' : '')};
  display: block;
  vertical-align: middle;
  width: calc(100% - 3rem);
  position: relative;
`;

const ListItemTitleIcon = styled.span`
  display: table-cell;
  position: absolute;
  right: 1rem;
  top: 50%;
  height: 1rem;
  width: 1rem;
  transform: translateY(-50%)
    ${(props) => (props.selected ? 'rotate(180deg)' : '')};
  transition: transform ${transitionSpeed} ease-in-out;
`;

const ListItemContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 2rem 0;
  ${breakpoint('medium')`
    padding-left: 1rem;
    padding-right: 1rem;
  `};

  &:before {
    top: 0;
    left: 0;
    height: 3px;
    content: '';
    position: absolute;
    width: 100%;
    background-color: currentColor;
    transform: translateX(${(props) => (props.selected ? '0%' : '-100%')});
    transition: transform ${transitionSpeed} ease-in-out;
  }
`;

const SlideDown = styled.div`
  max-height: ${(props) => props.contentHeight + 'px'};
  transition: max-height ${transitionSpeed} ease-out;
  overflow: hidden;
`;

class AccordionList extends React.Component<Props> {
  static Item = ListItem;

  constructor() {
    super();
    this.contentRefs = [];
  }

  state = {
    selectedContentHeight: 0,
  };

  componentDidUpdate() {
    this.setSelectedContentHeight();
  }

  componentDidMount() {
    window.addEventListener('resize', this.setSelectedContentHeight);
    this.setSelectedContentHeight();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setSelectedContentHeight);
  }

  setSelectedContentHeight = () => {
    const index = this.props.selectedIndex;

    const selectedContentHeight = this.contentRefs[index]
      ? this.contentRefs[index].clientHeight
      : 0;

    if (
      selectedContentHeight !== 0 &&
      selectedContentHeight !== this.state.selectedContentHeight
    ) {
      this.setState({ selectedContentHeight });
    }
  };

  handleSelect = (i: number) => {
    if (!this.props.onSelect) return;
    const iOut = i !== this.props.selectedIndex ? i : null;
    this.props.onSelect(iOut);
  };

  render() {
    return (
      <ListContainer>
        {React.Children.map(this.props.children, (child, i) => {
          const selected = this.props.selectedIndex === i;
          const contentHeight = selected ? this.state.selectedContentHeight : 0;
          return (
            <ListItemContainer selected={selected}>
              <ListItemTitle
                onClick={() => this.handleSelect(i)}
                data-testid={child.props.testid}
                role="button"
                aria-expanded={selected}
              >
                <ListItemTitleText selected={selected}>
                  {child.props.title}
                </ListItemTitleText>
                <ListItemTitleIcon selected={selected}>
                  <Expand />
                </ListItemTitleIcon>
              </ListItemTitle>
              <SlideDown contentHeight={contentHeight} aria-hidden={!selected}>
                <ListItemContent
                  ref={(r) => (this.contentRefs[i] = r)}
                  selected={selected}
                >
                  {child}
                </ListItemContent>
              </SlideDown>
            </ListItemContainer>
          );
        })}
      </ListContainer>
    );
  }
}

export default withTheme(AccordionList);
